import { extractOrgNumber } from './orgNumber';

// Pure row-state helpers for the Duett column + bulk send in
// components/dyrejournal/allVisitsTable.js.
// Duett v3 has NO paid status on an order: 'invoiceBooked' is bokført (posted to the
// ledger) and is reached one second after Order/Create (live-verified 2026-09-28), so a
// Duett order status must never be rendered as «Betalt». Mirrors the empty
// PAID_STATUSES in backend/lib/duett-payment-sync.js.

function isDuettTransferred(sale) {
    return !!(sale && sale.duettOrder && sale.duettOrder.orderId);
}

function isDuettCredited(sale) {
    return !!(sale && sale.duettCreditOrder && sale.duettCreditOrder.orderId);
}

const DUETT_ORDER_STATUS_LABELS = {
    saved: 'Lagret',
    orderPrepared: 'Klargjort',
    orderReadyToSend: 'Klar til fakturering',
    invoiceSent: 'Utfakturert',
    invoicePaymentProposal: 'Betalingsforslag',
    invoicePreBooked: 'Forhåndsbokført',
    invoiceBooked: 'Bokført',
};

// Norwegian label for a Duett OrderStatus; unknown values pass through, missing → ''.
function duettOrderStatusLabel(status) {
    if (!status) return '';
    return DUETT_ORDER_STATUS_LABELS[status] || String(status);
}

function isInvoiceSale(sale) {
    return !!sale && sale.paymentMethod === 'invoice';
}

function isInDuettVoucher(sale) {
    return !!(sale && sale.duettVoucher && sale.duettVoucher.voucherId);
}

function isPaidSale(sale) {
    return !!(sale && (sale.paidDate || sale.status === 1));
}

// An UNPAID invoice sale is the only thing that may become a Duett invoice
// (Tripletex parity, 2026-09-04). Duett's order model has no paid flag, so a
// paid sale cannot be transferred "as settled"; paid revenue reaches Duett via the
// dagsoppgjør voucher instead. Mirrors the backend guard in lib/duett-invoice.js
// (409 for paid, 400 for non-invoice) so the UI never offers a button that fails.
function isDuettInvoiceCandidate(sale) {
    return isInvoiceSale(sale) && !isPaidSale(sale);
}

// A credit-note sale from Dyrejournal's own "Krediter" flow (status 6 /
// paymentMethod 'creditNote', parentSaleId → the invoiced sale). Sent to Duett as an
// orderType 'credit' order against the parent's invoice (Tripletex parity, 2026-09-04).
function isCreditNoteSale(sale) {
    return !!sale && (sale.paymentMethod === 'creditNote' || sale.status === 6);
}

// Precedence: creditNote* > transferred > inVoucher > unsent/viaVoucher. An invoice
// sale that a clinic included in a dagsoppgjør voucher is ALREADY in Duett — it must
// show as inVoucher, never as unsent.
function duettRowState(sale) {
    if (isCreditNoteSale(sale) && !isDuettTransferred(sale)) {
        if (isDuettCredited(sale)) {
            return {
                kind: 'creditNoteSent',
                orderId: sale.duettCreditOrder.orderId,
                orderNumber: sale.duettCreditOrder.orderNumber || null,
                parentSaleId: sale.duettCreditOrder.parentSaleId || sale.parentSaleId || null,
            };
        }
        if (sale.parentSaleId) {
            return { kind: 'creditNoteUnsent', parentSaleId: sale.parentSaleId };
        }
        return { kind: 'creditNoteNoParent' };
    }
    if (isDuettTransferred(sale)) {
        const credited = isDuettCredited(sale);
        return {
            kind: 'transferred',
            orderId: sale.duettOrder.orderId,
            orderNumber: sale.duettOrder.orderNumber || null,
            invoiceNumber: sale.duettOrder.invoiceNumber || null,
            status: sale.duettOrder.status || null,
            credited,
            creditOrderId: credited ? sale.duettCreditOrder.orderId : null,
        };
    }
    if (isInDuettVoucher(sale)) {
        return { kind: 'inVoucher', voucherNumber: sale.duettVoucher.voucherNumber || null };
    }
    if (isDuettInvoiceCandidate(sale)) {
        return { kind: 'unsent' };
    }
    if (isInvoiceSale(sale)) {
        // Paid invoice sale: settled in Dyrejournal, reaches Duett via the voucher.
        return { kind: 'viaVoucher', paid: true };
    }
    return { kind: 'viaVoucher' };
}

const DISPATCH_SENT_KINDS = ['email', 'ehf', 'print'];

// Sent-status of a transferred row (Tripletex parity, 2026-09-04). Duett dispatches
// the invoice itself; the backend parses the Duett order log into
// duettOrder.dispatch { kind: email|ehf|print|failed, at, description } and stamps
// duettInvoiceSent for a real dispatch (transfer read-back + payment sync).
//   sent        — Duett confirmed e-mail / EHF / print (print = Duett prints and posts)
//   printed     — no Duett dispatch, but "Skriv ut" in Dyrejournal (sent by post)
//   failed      — Duett logged a delivery/sending failure and nothing was printed
//   unconfirmed — no dispatch line yet («Oppdater status fra Duett» / the sync script fills it)
// `fallback` (sent only): Duett silently downgrades the requested send option —
// autoTrans (EHF) needs org number + contact person on the card, ELMA registration
// and an AutoTrans agreement on the client, else e-mail if available, else print
// (Duett support 2026-09-10). The order log only shows the method actually used, so
// requested (duettOrder.invoiceSendOption) vs method is the only way to surface it.
// Returns null for rows without a Duett order.
function isDuettSendFallback(requested, method) {
    if (!requested || !method) return false;
    if (requested === 'autoTrans') return method !== 'ehf';
    if (requested === 'email') return method === 'print';
    return false;
}

function duettSentState(sale) {
    if (!isDuettTransferred(sale)) return null;
    const dispatch = sale.duettOrder.dispatch || null;
    const kind = dispatch ? dispatch.kind : null;
    const requested = sale.duettOrder.invoiceSendOption || null;
    if (DISPATCH_SENT_KINDS.includes(kind)) {
        return {
            state: 'sent',
            method: kind,
            at: dispatch.at || sale.duettInvoiceSent || null,
            description: dispatch.description || '',
            requested,
            fallback: isDuettSendFallback(requested, kind),
        };
    }
    if (sale.duettInvoiceSent) {
        return { state: 'sent', method: null, at: sale.duettInvoiceSent, description: '', requested, fallback: false };
    }
    if (sale.printedDate) {
        return { state: 'printed', method: null, at: sale.printedDate, description: '' };
    }
    if (kind === 'failed') {
        return { state: 'failed', method: null, at: null, description: dispatch.description || '' };
    }
    return { state: 'unconfirmed', method: null, at: null, description: '' };
}

// Default for the drawer's send-option select. Mirrors backend
// lib/duett-invoice.js pickInvoiceSendOption (Tripletex / PowerOffice parity):
// pinned visitor.invoiceType ('EHF' → autoTrans, 'EMAIL' → e-mail), else an
// org-number customer → autoTrans (EHF), else e-mail when the visitor has one, else
// print (Duett's print service posts it). orgNumber is free text, so extract first.
function defaultDuettSendOption(visitor) {
    if (!visitor) return 'print';
    const invoiceType = String(visitor.invoiceType || '').toUpperCase();
    if (invoiceType === 'EHF') return 'autoTrans';
    const hasEmail = !!(visitor.invoiceEmail || visitor.email);
    if (invoiceType === 'EMAIL') return hasEmail ? 'email' : 'print';
    if (extractOrgNumber(visitor.orgNumber)) return 'autoTrans';
    return hasEmail ? 'email' : 'print';
}

// Unpaid invoice sales only: cash/card revenue reaches Duett via the dagsoppgjør
// voucher — per-sale sending would double-post revenue and invoice already-paid
// customers. A voucher-included invoice sale is likewise already posted.
function qualifiesForDuettBulkSend(sale) {
    return isDuettInvoiceCandidate(sale)
        && !isDuettTransferred(sale)
        && !isInDuettVoucher(sale);
}

export {
    isDuettTransferred,
    isDuettCredited,
    duettOrderStatusLabel,
    isDuettInvoiceCandidate,
    isCreditNoteSale,
    duettRowState,
    duettSentState,
    isDuettSendFallback,
    defaultDuettSendOption,
    qualifiesForDuettBulkSend,
};
